import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useTicketingStore, {
  TicketFilters,
  TicketSearchByEnum,
  TicketSortKeyEnum,
} from '@/stores/useTicketingStore';
import { scrollTop } from '@/utils';

const TICKET_CENTER_PATH = '/ticket-center';

interface TicketsUrlSyncResult {
  currentPage: number;
  ticketFilters: TicketFilters | undefined;
  sortKey: TicketSortKeyEnum;
  searchBy: TicketSearchByEnum | undefined;
  searchText: string | undefined;
  setCurrentPage: (page: number) => void;
  setTicketFilters: (filters: TicketFilters | undefined) => void;
  handleSearch: () => void;
  handleResetFilters: () => void;
  handlePaginationChange: (page: number) => void;
}

export function useTicketsUrlSync(): TicketsUrlSyncResult {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { statusFilter, createdAtFilter, searchBy, searchText, sortKey, setCreatedAt, setStatus } =
    useTicketingStore();

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) || 1 : 1;
  });

  const [ticketFilters, setTicketFilters] = useState<TicketFilters | undefined>(undefined);

  // Track previous URL string to avoid redundant setSearchParams calls
  const prevUrlRef = useRef<string>(searchParams.toString());

  // ─── Guard: only run URL sync on /ticket-center exactly ───
  const isTicketCenterRoute = location.pathname === TICKET_CENTER_PATH;

  // ─── URL → State (on mount only) ───
  useEffect(() => {
    if (!isTicketCenterRoute) return;

    const pageParam = searchParams.get('page');
    if (pageParam) {
      const parsed = parseInt(pageParam, 10);
      if (!isNaN(parsed) && parsed > 0) {
        setCurrentPage(parsed);
      }
    }
     
  }, []);

  // ─── State → URL (when page/sort/filters change) ───
  useEffect(() => {
    if (!isTicketCenterRoute) return;

    const params = new URLSearchParams();

    // Page
    params.set('page', currentPage.toString());

    // Sort (only write non-default)
    if (sortKey !== TicketSortKeyEnum.CREATED_DESC) {
      params.set('sort', sortKey);
    }

    const newUrl = params.toString();

    // Deep comparison: skip if URL is identical
    if (newUrl === prevUrlRef.current) return;

    prevUrlRef.current = newUrl;
    setSearchParams(params, { replace: true });
  }, [currentPage, sortKey, isTicketCenterRoute]);

  // ─── Reset page when sort changes (skip initial mount) ───
  const sortMountedRef = useRef(false);
  useEffect(() => {
    if (!isTicketCenterRoute) return;
    if (!sortMountedRef.current) {
      sortMountedRef.current = true;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [sortKey]);

  // ─── Reset page when search changes ───
  const prevSearchRef = useRef({ searchBy, searchText });
  useEffect(() => {
    if (!isTicketCenterRoute) return;

    const prevSearch = prevSearchRef.current;
    const searchChanged = prevSearch.searchBy !== searchBy || prevSearch.searchText !== searchText;

    // Update ref for next comparison
    prevSearchRef.current = { searchBy, searchText };

    // Only reset if search actually changed and we're not already on page 1
    if (searchChanged && (searchBy || searchText) && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchBy, searchText, isTicketCenterRoute, currentPage]);

  // ─── Scroll to top on page change ───
  useEffect(() => {
    scrollTop();
  }, [currentPage]);

  // ─── Clear filters when searching by ticket ID ───
  useEffect(() => {
    if (!isTicketCenterRoute) return;
    if (searchBy === TicketSearchByEnum.TICKET_ID) {
      setTicketFilters(undefined);
      setCreatedAt(undefined);
      setStatus([]);
    }
  }, [searchBy, searchText]);

  // ─── Cleanup on navigate away ───
  useEffect(() => {
    return () => {
      if (!location.pathname.startsWith(TICKET_CENTER_PATH)) {
        // Optionally reset store state when leaving ticket center
      }
    };
  }, [location.pathname]);

  // ─── Handlers ───
  const handleSearch = useCallback(() => {
    if (!isTicketCenterRoute) return;

    // Read fresh values from the store to avoid stale closure issues
    // (e.g. when setStatus is called immediately before handleSearch)
    const { statusFilter: freshStatus, createdAtFilter: freshDate } =
      useTicketingStore.getState();

    setCurrentPage(1);
    setTicketFilters({
      statusFilter: freshStatus,
      createdAtFilter: freshDate,
    });
  }, [isTicketCenterRoute]);

  const handleResetFilters = useCallback(() => {
    if (!isTicketCenterRoute) return;

    setTicketFilters(undefined);
    setCurrentPage(1);
  }, [isTicketCenterRoute]);

  const handlePaginationChange = useCallback(
    (page: number) => {
      if (!isTicketCenterRoute) return;
      setCurrentPage(page);
    },
    [isTicketCenterRoute],
  );

  return {
    currentPage,
    ticketFilters,
    sortKey,
    searchBy,
    searchText,
    setCurrentPage,
    setTicketFilters,
    handleSearch,
    handleResetFilters,
    handlePaginationChange,
  };
}
